import React, { useEffect, useMemo, useState } from 'react';
import { useLocale } from '../context/LocaleContext';
import s from './DateRangePicker.module.css';

// A start/end date range, picked in one calendar.
//
// One calendar rather than two date fields, because the teacher is choosing a
// span: MMS puts both endpoints in one popover with the days between them
// shaded, so "the last quarter" is visible as a shape and an inverted range is
// obvious on sight.
//
// All arithmetic is on UTC calendar grids: a date-only value built in local
// time lands a day out for anyone west of Greenwich.

// The day names and the month's title are the Region's (#534): `Mo Di Mi` and
// `Oktober 2026` in Germany, as MMS's own calendar writes them, `Sun Mon Tue`
// and `October 2026` in the United States.

const iso = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

// The six-week grid a month is drawn on, including the adjacent-month days that
// fill the corners. Six rows always, so the popover does not change height as
// you page through months — a control that resizes under the cursor makes you
// re-aim mid-click.
//
// Each row begins on the studio's first weekday, its Region's (#505), as the
// calendar's month does: `weekStartsOn`, 0 Sunday .. 6 Saturday.
export function monthGrid(year, month, weekStartsOn) {
  const lead = (new Date(Date.UTC(year, month, 1)).getUTCDay() - weekStartsOn + 7) % 7;
  const start = new Date(Date.UTC(year, month, 1 - lead));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(Date.UTC(
      start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + i
    ));
    return {
      value: iso(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
      day: d.getUTCDate(),
      outside: d.getUTCMonth() !== month,
    };
  });
}

export default function DateRangePicker({ start, end, onChange, min, max, separator = 'and' }) {
  // The two ends in the studio's short date, as every other date on the page;
  // a dash for none.
  const { formatDate, weekStartsOn, weekdayNames, formatMonth } = useLocale();
  const formatShort = (value) => formatDate(value) || '—';
  const [open, setOpen] = useState(null); // null | 'start' | 'end'
  const [view, setView] = useState(() => {
    const [y, m] = String(start || '').split('-').map(Number);
    return y ? { year: y, month: m - 1 } : { year: 2000, month: 0 };
  });

  // Re-centre on whichever endpoint is being edited, so opening "End" does not
  // leave you looking at the month the start happens to be in.
  useEffect(() => {
    if (!open) return;
    const [y, m] = String(open === 'start' ? start : end || start).split('-').map(Number);
    if (y) setView({ year: y, month: m - 1 });
  }, [open, start, end]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const grid = useMemo(() => monthGrid(view.year, view.month, weekStartsOn), [view, weekStartsOn]);
  const dayNames = useMemo(() => weekdayNames.map((_, i) => weekdayNames[(i + weekStartsOn) % 7]), [weekStartsOn, weekdayNames]);

  const pick = (value) => {
    if (min && value < min) return;
    if (max && value > max) return;
    if (open === 'start') {
      // Dragging the start past the end carries the end with it rather than
      // producing a range that matches nothing.
      onChange({ start: value, end: value > end ? value : end });
      setOpen('end');
      return;
    }
    onChange({ start: value < start ? value : start, end: value });
    setOpen(null);
  };

  const step = (delta) => setView((v) => {
    const d = new Date(Date.UTC(v.year, v.month + delta, 1));
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
  });

  return (
    <>

      <span className={s.picker}>
        <button className={s.trigger} onClick={() => setOpen(open === 'start' ? null : 'start')}>
          {formatShort(start)} ▾
        </button>
        {/* Both triggers live here so one popover serves them, and the caller
            still reads as the sentence MMS writes: "between X and Y". */}
        <span className={s.separator}>{separator}</span>
        <button className={s.trigger} onClick={() => setOpen(open === 'end' ? null : 'end')}>
          {formatShort(end)} ▾
        </button>
        {open ? (
          <>
            <div className={s.scrim} onClick={() => setOpen(null)} />
            <div className={s.pop} role="dialog" aria-label="Choose a date range">
              <div className={s.tabs}>
                {['start', 'end'].map((which) => (
                  <button key={which}
                          className={open === which ? s.tabOn : s.tab}
                          onClick={() => setOpen(which)}>
                    <span className={s.tabLabel}>{which === 'start' ? 'Start' : 'End'}</span>
                    <span className={s.tabValue}>{formatShort(which === 'start' ? start : end)}</span>
                  </button>
                ))}
              </div>

              <div className={s.head}>
                <span className={s.title}>{formatMonth(`${view.year}-${String(view.month + 1).padStart(2, '0')}`)}</span>
                <button className={s.nav} onClick={() => step(-1)} aria-label="Previous month">‹</button>
                <button className={s.nav} onClick={() => step(1)} aria-label="Next month">›</button>
              </div>

              <div className={s.grid}>
                {dayNames.map((d) => <span key={d} className={s.dow}>{d}</span>)}
                {grid.map((cell) => {
                  const edge = cell.value === start || cell.value === end;
                  const inside = cell.value > start && cell.value < end;
                  const blocked = (min && cell.value < min) || (max && cell.value > max);
                  return (
                    <button
                      key={cell.value}
                      className={edge ? s.dayEdge : inside ? s.dayIn : cell.outside ? s.dayOutside : s.day}
                      disabled={blocked}
                      onClick={() => pick(cell.value)}>
                      {cell.day}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}
      </span>
    </>
  );
}
